import type {
  ApiNotificationsResponse,
  ApiNotificationResponse,
  ApiMarkAsReadResponse,
  ApiMarkAllAsReadResponse,
  GetNotificationsFilters,
} from "@/types/entities/notifications";
import axios from "@/utils/axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const NOTIFICATIONS_QUERY_KEY = "notifications";

/**
 * Fetch notifications list
 */
const getNotifications = async (
  filters: GetNotificationsFilters = {},
): Promise<ApiNotificationsResponse> => {
  const response = await axios.get("/admin/v1/notifications", {
    params: filters,
  });
  return response.data;
};

/**
 * Hook to get notifications list with filters
 */
export const useGetNotifications = (
  filters: GetNotificationsFilters = {},
  options?: { enabled?: boolean; refetchInterval?: number },
) => {
  return useQuery({
    queryKey: [NOTIFICATIONS_QUERY_KEY, filters],
    queryFn: () => getNotifications(filters),
    enabled: options?.enabled ?? true,
    refetchInterval: options?.refetchInterval,
  });
};

/**
 * Fetch single notification by ID
 */
const getNotification = async (
  id: string,
): Promise<ApiNotificationResponse> => {
  const response = await axios.get(`/admin/v1/notifications/${id}`);
  return response.data;
};

/**
 * Hook to get single notification
 */
export const useGetNotification = (id: string, enabled = true) => {
  return useQuery({
    queryKey: [NOTIFICATIONS_QUERY_KEY, id],
    queryFn: () => getNotification(id),
    enabled: !!id && enabled,
  });
};

/**
 * Mark notification as read
 */
const markAsRead = async (id: string): Promise<ApiMarkAsReadResponse> => {
  const response = await axios.put(
    `/admin/v1/notifications/${id}/mark-as-read`,
  );
  return response.data;
};

/**
 * Hook to mark notification as read
 */
export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markAsRead,
    onSuccess: () => {
      // Invalidate notifications list to refresh unread count
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });
    },
  });
};

/**
 * Mark all notifications as read
 */
const markAllAsRead = async (): Promise<ApiMarkAllAsReadResponse> => {
  const response = await axios.put("/admin/v1/notifications/mark-all-as-read");
  return response.data;
};

/**
 * Hook to mark all notifications as read
 */
export const useMarkAllAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markAllAsRead,
    onSuccess: () => {
      // Invalidate notifications list to refresh
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });
    },
  });
};

export { NOTIFICATIONS_QUERY_KEY };

/**
 * Mark notification as unread
 */
const markAsUnread = async (id: string): Promise<ApiMarkAsReadResponse> => {
  const response = await axios.put(
    `/admin/v1/notifications/${id}/mark-as-unread`,
  );
  return response.data;
};

/**
 * Hook to mark notification as unread
 */
export const useMarkAsUnread = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markAsUnread,
    onSuccess: () => {
      // Invalidate notifications list to refresh unread count
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });
    },
  });
};
